import { Modal, Select, Spin } from "antd";
import React from "react";
import { useGetExistingComponentsQuery } from "../../generated/types";
import { Project } from "../../types";
import { ComponentsWithAncestors } from "../ComponentsWithAncestors";
import styles from "./ConfigPlansBottomRibbon.module.css";

export interface ComponentForDisplay {
  name: string;
  id: string;
  tags: string[];
  guiIndex: number | null;
  ancestors: { name: string; guiIndex: number }[];
}

interface EditComponentTagsModalProps {
  title: string;
  actionText: string;
  allowNewTagCreation: boolean;
  visible: boolean;
  onCancel: () => void;
  onSubmit: (
    tags: string[],
    components: { id: string; tags: string[] }[],
  ) => void;
  componentIds: string[];
  project: Project;
  planId: string | undefined;
  allComponentTags: string[];
}

export const EditComponentTagsModal = (props: EditComponentTagsModalProps) => {
  const [tags, setTags] = React.useState<string[]>([]);

  const { data: existingComponents, loading } = useGetExistingComponentsQuery({
    skip: !props.visible || !props.planId || props.componentIds.length === 0,
    variables: {
      customer: props.project.customer,
      project: props.project.project,
      scope: props.project.scope,
      componentIds: props.componentIds,
      planId: props.planId as string,
    },
  });

  const components: ComponentForDisplay[] =
    existingComponents?.componentsByIds?.map((c) => ({
      guiIndex: c.guiIndex ?? null,
      name: c.name,
      id: c.id,
      tags: c.tags,
      ancestors: c.parentTask
        ? [
            ...c.parentTask.ancestors,
            { name: c.parentTask.name, guiIndex: c.parentTask.guiIndex },
          ]
        : [],
    })) ?? [];

  const handleChange = (value: string[]) => {
    setTags(value);
  };

  const handleSubmit = () => {
    const componentsForSubmit = (existingComponents?.componentsByIds ?? []).map(
      (c) => ({ id: c.id, tags: c.tags }),
    );
    props.onSubmit(tags, componentsForSubmit);
  };

  const canSubmit = !loading && components.length > 0;

  return (
    <Modal
      title={props.title}
      width="70%"
      destroyOnClose
      visible={props.visible}
      onOk={handleSubmit}
      onCancel={props.onCancel}
      okButtonProps={{ disabled: !canSubmit }}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Select
            mode={props.allowNewTagCreation ? "tags" : "multiple"}
            className={styles["modal-select-width"]}
            onChange={handleChange}
          >
            {props.allComponentTags.map((tag) => (
              <Select.Option key={tag} value={tag}>
                {tag}
              </Select.Option>
            ))}
          </Select>
          <div className={styles["modal-box"]}>
            <div className={styles["modal-box-text"]}>
              You are <b>{props.actionText}</b> the selected tags for the
              following components:
            </div>
            <ComponentsWithAncestors components={components} />
          </div>
        </>
      )}
    </Modal>
  );
};
